import type { FunctionalComponent } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { StoreContext, useWidgetStore } from './store';
import { getWidgetEventOpenUrl } from './lib/eventShareUrl';
import { isWidgetAppIntroDismissedInSession, WidgetAppIntro } from './components/WidgetAppIntro';
import { fetchWidgetData, recordWidgetView, verifyPromoCode, releaseHold, deleteEventTablesHold } from './api/client';
import { buildSetDataPayloadFromWidgetResponse } from './lib/widgetDataPayload';
import {
  hasWidgetTableReservationPath,
  hasWidgetTicketTiers,
  isWidgetHybridBooking,
  isWidgetInTablePath,
} from './lib/widgetBookingPath';
import type { WidgetBookingPath } from './types';
import { applyTheme, detectColorScheme } from './utils/theme';
import { listenForParentMessages } from './utils/postMessage';
import { getEventOccurrenceEntries, getPurchasableOccurrenceEntries } from './lib/eventOccurrenceDates';
import { applyOccurrenceDateSelection } from './lib/applyOccurrenceSelection';
import { useAutoResize } from './hooks/useAutoResize';
import { EventHeader } from './components/EventHeader';
import { EventInfoBlock } from './components/EventInfoBlock';
import { EventHeroDateControl } from './components/EventHeroDateControl';
import { EventHeroQr } from './components/EventHeroQr';
import { FAQ } from './components/FAQ';
import { TicketSelection } from './steps/TicketSelection';
import { TableReservationSelection } from './steps/TableReservationSelection';
import { SponsorVideoOverlay } from './steps/SponsorVideo';
import { PromoCode } from './steps/PromoCode';
import { ContactForm } from './steps/ContactForm';
import { Payment } from './steps/Payment';
import { Confirmation } from './steps/Confirmation';
import { BookingPathSwitch } from './components/BookingPathSwitch';
import './appIntro.css';

interface Props {
  widgetId: string;
  prefillContact?: { name?: string; email?: string; phone?: string };
  /** Referral code from ?ref= in URL — passed into sale metadata */
  refCode?: string;
  /** When true (default), show registration modal after purchase if shouldRegister is true; pass false to disable */
  supportRegistration?: boolean;
}

const WIDGET_TAB_TITLE_BASE = 'Meeteor Reservations';

export const App: FunctionalComponent<Props> = ({ widgetId, prefillContact, refCode, supportRegistration }) => {
  const { state, dispatch } = useWidgetStore(widgetId, prefillContact, refCode, supportRegistration);
  const [appIntroDismissed, setAppIntroDismissed] = useState(() => isWidgetAppIntroDismissedInSession());

  useEffect(() => {
    const eventName = state.event?.name?.trim();
    if (eventName) {
      document.title = `${WIDGET_TAB_TITLE_BASE} | ${eventName}`;
      let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'description';
        document.head.appendChild(meta);
      }
      meta.content = `${WIDGET_TAB_TITLE_BASE} | ${eventName}`;
    } else {
      document.title = WIDGET_TAB_TITLE_BASE;
    }
  }, [state.event?.name, state.event]);

  useAutoResize({
    layout: state.widget?.layout,
    max_width: state.widget?.max_width,
  });

  useEffect(() => {
    async function load() {
      try {
        const res = await fetchWidgetData(widgetId);
        const payload = buildSetDataPayloadFromWidgetResponse(res);
        const all = getEventOccurrenceEntries(payload.event);
        const purchasable = getPurchasableOccurrenceEntries(all);
        const def = purchasable[0];
        const needLinkedEvent =
          def?.targetEventId && def.targetEventId !== payload.event._id;
        if (needLinkedEvent && def) {
          await applyOccurrenceDateSelection(dispatch, widgetId, payload.event, def);
        } else {
          const scheme = detectColorScheme();
          applyTheme(
            res.data.widget.theme,
            scheme === 'dark' ? true : scheme === 'light' ? false : undefined,
          );
          dispatch({ type: 'SET_DATA', payload });
        }
        recordWidgetView(widgetId);
      } catch (e: any) {
        dispatch({ type: 'SET_ERROR', message: e.message || 'Failed to load widget' });
      }
    }

    load();
  }, [widgetId, dispatch]);

  useEffect(() => {
    return listenForParentMessages(
      (code) => {
        if (state.event) {
          verifyPromoCode(state.event._id, code)
            .then((result) => {
              dispatch({
                type: 'SET_DISCOUNT',
                discount: {
                  code: result.code,
                  type: result.type,
                  value: result.value,
                  source: 'promo',
                },
              });
            })
            .catch(() => {});
        }
      },
      (theme) => {
        if (state.widget) {
          const merged = { ...state.widget.theme, ...theme };
          const darkOverride = (theme as any).dark === true ? true : (theme as any).dark === false ? false : undefined;
          applyTheme(merged as any, darkOverride);
        }
      },
      (contact) => {
        dispatch({
          type: 'SET_CONTACT',
          contact: {
            name: contact.name || state.contact.name,
            email: contact.email || state.contact.email,
            phone: contact.phone || state.contact.phone,
          },
        });
      },
    );
  }, [state.event, state.widget]);

  async function handleBack() {
    if (
      (state.step === 'contact' || state.step === 'payment') &&
      state.holdId &&
      state.event
    ) {
      if (state.holdApiSource === 'event_tables') {
        try {
          await deleteEventTablesHold(state.event._id, state.holdId);
        } catch {
          // still navigate back
        }
      } else {
        try {
          await releaseHold(state.widgetId, state.holdId);
        } catch {
          // still navigate back
        }
      }
      dispatch({ type: 'CLEAR_HOLD' });
    }
    switch (state.step) {
      case 'contact':
        dispatch({ type: 'SET_STEP', step: 'tickets' });
        break;
      case 'payment':
        dispatch({ type: 'SET_STEP', step: 'contact' });
        break;
      default:
        break;
    }
  }

  const hasTableResPath = hasWidgetTableReservationPath(state.event, state.venue);
  const hasTicketTiers = hasWidgetTicketTiers(state.event, state.feeStructures);
  const hybridBooking = isWidgetHybridBooking(state.event, state.venue, state.feeStructures);
  const inTablePath = isWidgetInTablePath(
    hasTableResPath,
    hasTicketTiers,
    state.widgetBookingPath,
  );
  const showBack = ['contact', 'payment'].includes(state.step) && !inTablePath;
  const stepTitle: Record<string, string> = {
    tickets: 'Tickets',
    contact: 'Your Info',
    payment: 'Payment',
  };

  const isDesktopLayout = state.widget?.layout === 'desktop';
  const showEventImage = state.widget?.show_event_image && state.event?.feature_image;

  const tableFlowActive =
    inTablePath && ['tickets', 'contact', 'payment', 'confirmation'].includes(state.step);

  const handleBookingPathChange = useCallback(
    async (path: WidgetBookingPath) => {
      if (path === state.widgetBookingPath) return;
      if (path === 'tickets' && state.holdId && state.event) {
        if (state.holdApiSource === 'event_tables') {
          try {
            await deleteEventTablesHold(state.event._id, state.holdId);
          } catch {
            /* still switch path */
          }
        } else if (state.holdApiSource === 'widget' && state.widgetId) {
          try {
            await releaseHold(state.widgetId, state.holdId);
          } catch {
            /* still switch */
          }
        }
      }
      dispatch({ type: 'SET_WIDGET_BOOKING_PATH', path });
    },
    [
      state.widgetBookingPath,
      state.holdId,
      state.holdApiSource,
      state.event,
      state.widgetId,
      dispatch,
    ],
  );

  const showAppIntro =
    !appIntroDismissed &&
    state.step === 'tickets' &&
    state.event != null &&
    state.widget != null;
  const shareUrl = state.event ? getWidgetEventOpenUrl(state.event, refCode) : '';
  const introPrimary = state.widget?.theme?.primary_color || '#5340e4';

  const renderFlowContent = () => (
    <div class="mw-flow-card">
      {showBack && (
        <div class="mw-nav">
          <button type="button" class="mw-back-btn" onClick={handleBack}>
            ←
          </button>
          <span class="mw-nav-title">{stepTitle[state.step] || ''}</span>
        </div>
      )}
      {hybridBooking && state.step === 'tickets' && (
        <BookingPathSwitch
          value={state.widgetBookingPath}
          onChange={handleBookingPathChange}
        />
      )}
      {tableFlowActive && <TableReservationSelection />}
      {!inTablePath && state.step === 'tickets' && (
        <>
          <PromoCode />
          <TicketSelection />
        </>
      )}
      {state.step === 'contact' && !inTablePath && <ContactForm />}
      {state.step === 'payment' && !inTablePath && <Payment />}
    </div>
  );

  return (
    <StoreContext.Provider value={{ state, dispatch }}>
      {showAppIntro && (
        <WidgetAppIntro
          variant={inTablePath ? 'table' : 'nonTable'}
          eventName={state.event?.name || ''}
          shareUrl={shareUrl}
          primaryColor={introPrimary}
          onContinue={() => setAppIntroDismissed(true)}
        />
      )}

      <div
        class={`mw-root mw-layout--${state.widget?.layout || 'vertical'}`}
        style={
          isDesktopLayout
            ? {
                '--mw-desktop-max-width': state.widget?.max_width
                  ? `${Math.min(1200, state.widget.max_width)}px`
                  : '1200px',
                '--mw-desktop-margin-top': (() => {
                  const mt = state.widget?.margin_top;
                  const n = typeof mt === 'string' ? parseInt(mt, 10) : mt;
                  return typeof n === 'number' && !isNaN(n) && n >= 0 ? `${n}px` : '0';
                })(),
              }
            : undefined
        }
      >
        <div class="mw-card">
          {state.step === 'loading' && (
            <div class="mw-loading">
              <div class="mw-spinner" />
              <span style={{ fontSize: '0.85rem', color: 'var(--mw-text-muted)' }}>
                Loading tickets...
              </span>
            </div>
          )}

          {state.step === 'error' && (
            <div class="mw-error-screen">
              <h2>Something went wrong</h2>
              <p>{state.errorMessage}</p>
              <button
                type="button"
                class="mw-btn mw-btn--outline"
                onClick={() => window.location.reload()}
              >
                Try again
              </button>
            </div>
          )}

          {isDesktopLayout &&
            state.step !== 'loading' &&
            state.step !== 'error' && (
              <div class="mw-desktop-layout">
                {showEventImage && (
                  <div class="mw-desktop-hero-wrap">
                    <img
                      class="mw-desktop-image"
                      src={state.event!.feature_image}
                      alt={state.event!.name}
                      loading="lazy"
                    />
                    <EventHeroQr shareUrl={shareUrl} eventName={state.event?.name} />
                    <EventHeroDateControl />
                  </div>
                )}
                <div class="mw-desktop-grid">
                  <div class="mw-desktop-left mw-desktop-info">
                    <EventInfoBlock />
                  </div>
                  <div class="mw-desktop-right">
                    {(state.step !== 'confirmation' || inTablePath) && renderFlowContent()}
                    {state.step === 'confirmation' && !inTablePath && <Confirmation />}
                  </div>
                  <div class="mw-desktop-left mw-desktop-faq">
                    <FAQ />
                  </div>
                </div>
                <SponsorVideoOverlay />
              </div>
            )}

          {!isDesktopLayout && state.step !== 'loading' && state.step !== 'error' && (state.step !== 'confirmation' || inTablePath) && (
            <div class="mw-mobile-layout">
              {state.step === 'tickets' && <EventHeader />}
              <div class="mw-mobile-flow">{renderFlowContent()}</div>
              <div class="mw-mobile-faq"><FAQ /></div>
            </div>
          )}

          {!isDesktopLayout && state.step === 'confirmation' && !inTablePath && <Confirmation />}
          {!isDesktopLayout && <SponsorVideoOverlay />}
        </div>

        {state.step !== 'confirmation' && (
          <div class="mw-powered">
            Powered by{' '}
            <a href="https://meeteor.me" target="_blank" rel="noopener noreferrer">
              Meeteor
            </a>
          </div>
        )}
      </div>
    </StoreContext.Provider>
  );
};
